import React, { useState, useRef } from 'react';
import { Button, Chip, Typography, CircularProgress } from '@mui/material';
import { dateFormater, modalOption } from "../../bmr/utils/common/utils";
import { notify } from '../../bmr/utils/methods/notification';
import taskApi from '../../services/taskService';
import { useSelector } from 'react-redux';
import { hasCRPermission } from '../../utils/utils';

const ChangeRequestModal = ({
  taskData,
  onApprove,
  onReject,
  onClose
}) => {
  const [loading, setLoading] = useState(false);
  const [actionType, setActionType] = useState(null); // 'approve' or 'reject'
  const isProcessing = useRef(false);

  const crPermissions = useSelector((state) => state.loginData?.cr_permissions);
  const crSlug = taskData?.change_request_reason_value?.[0]?.slug;
  const role_id = useSelector((state) => state.loginData.role_id);

  const canApprove = role_id == 1 || hasCRPermission(crPermissions, [crSlug], "APPROVE");

  // Extract data from taskData with fallbacks
  const patientName = taskData?.patient
    ? `${taskData.patient.first_name} ${taskData.patient.last_name}`.trim()
    : "--";

  const phase = taskData?.phase?.title;
  const hospital = taskData?.patient?.investigator?.hospital_details?.name;
  const phaseType = taskData?.phase?.phase_type_details?.name;
  const phaseTypeNameLower = (phaseType || "").toLowerCase();
  const personLabel = phaseTypeNameLower === "trial" ? "Subject" : "Patient";
  // Patient IDs - adjust these based on your actual data structure
  const patientId = taskData?.patient?.patient_id;
  const hospitalPatientId = taskData?.hospital_patient_id;

  const crRaisedBy = taskData?.creator
    ? `${taskData.creator.name}`.trim()
    : "--";

  const requestedDate = taskData?.createdAt
    ? dateFormater(taskData.createdAt)
    : "N/A";

  const reason = taskData?.change_request_reason_value
    ?.map(item => item.title)
    .join(', ') || '';
  // Get comment from description or other fields
  const comment = taskData?.description;
  const handleUpdateTaskStatus = async (status) => {
    if (isProcessing.current) {
      return;
    }
    if (!taskData?.id) {
      notify.error("Task ID not found");
      return;
    }
    isProcessing.current = true;
    setLoading(true);
    setActionType(status === 0 ? 'approve' : 'reject');
    try {
      await taskApi.approveRejectTask(taskData.id, status);
      const findOne = modalOption?.includes(taskData?.change_request_reason_value?.[0]?.slug)
      notify.success(
        status === 0
          ? "Task approved successfully!"
          : "Task rejected successfully!"
      )
      if (status === 0 && onApprove) {
        onApprove(taskData, findOne);
      } else if (status === 1 && onReject) {
        onReject(taskData);
      }
    } catch (error) {
      console.error('Error updating task status:', error);
      let errorMessage = 'Failed to update task status';
      if (error.response) {
        // The request was made and the server responded with a status code
        errorMessage = error.response.data?.message ||
          error.response.data?.error ||
          `Server error: ${error.response.status}`;
      } else if (error.request) {
        // The request was made but no response was received
        errorMessage = 'No response from server. Please check your connection.';
      } else {
        // Something happened in setting up the request
        errorMessage = error.message;
      }

      notify.error(errorMessage);
    } finally {
      setLoading(false);
      setActionType(null);
      onClose();
      isProcessing.current = false;
    }
  };

  const handleReject = () => {
    handleUpdateTaskStatus(1);
  };

  const handleApprove = () => {
    handleUpdateTaskStatus(0);
  };

  return (
    <div className="space-y-6">
      {/* Main Info Section */}
      <div className="m-0 p-3 border-b border-[#DDDDDD] text-center">
        <Typography variant="h6" className="font-semibold text-gray-800">
          Change Request raised for {reason}
        </Typography>
      </div>

      <div className="space-y-4">
        {/* First row: Patient name, phase, trial chip and Requested Date */}
        <div className="flex justify-between items-start">
          <div className="flex items-center gap-3">
            <div>
              <div className="font-bold text-lg">
                {patientName} | {phase}
                <Chip
                  color='success'
                  size="small"
                  // sx={{
                  //   backgroundColor: "rgba(8, 144, 223, 0.81) !important",
                  //   color: "white",
                  //   marginLeft: "8px"
                  // }} 
                  label={phaseType}
                />
              </div>
              <div className="mt-1">
                {hospital}
              </div>
            </div>
            {/* <div className="bg-blue-100 text-blue-800 text-xs font-semibold px-3 py-1 rounded-full">
              TRIAL
            </div> */}
          </div>

          <div className="flex items-center gap-2">
            <div className="text-gray-500 text-sm">
              <Typography>Requested Date</Typography>
            </div>
            <span className=''>{requestedDate}</span>
          </div>
        </div>

        <hr className="border-gray-300" />

        {/* Patient Details in 3 columns */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Patient/Subject ID Column */}
          <div>
            <div className="text-gray-500 text-sm mb-1">
              <Typography>{personLabel} ID:</Typography>
            </div>
            <div className="">{patientId}</div>
          </div>

          {/* Hospital Patient ID Column */}
          <div>
            <div className="text-gray-500 text-sm mb-1">
              <Typography>Hospital {personLabel} ID:</Typography>
            </div>
            <div className="">{hospitalPatientId}</div>
          </div>

          {/* CR Raised By Column */}
          <div>
            <div className="text-gray-500 text-sm mb-1">
              <Typography>CR Raised By:</Typography>
            </div>
            <div className="">{crRaisedBy}</div>
          </div>
        </div>

        {/* Change Request Reason */}
        <div>
          <h3 className="text-gray-500 text-sm mb-1">
            <Typography>Reason:</Typography>
          </h3>
          <div className="">
            {reason}
          </div>
        </div>
        {/* Comment Section */}
        <div>
          <h3 className="text-gray-500 text-sm mb-1">Comment:</h3>
          <div className="">
            {comment}
          </div>
        </div>

        <hr className="border-gray-300" />

        {/* Action Buttons Centered */}
        <div className="flex justify-center space-x-4 pt-2">
          {/* Reject Button */}
          {canApprove && (
            <Button
              variant="outlined"
              onClick={handleReject}
              disabled={loading}
              sx={{
                width: "125px !important",
                height: "36px !important",
                backgroundColor: "transparent !important",
                border: "1.5px solid #d32f2f !important",
                color: "#d32f2f !important",
                boxShadow: "none !important",
                textTransform: "none",
                borderRadius: "5px !important",
                fontWeight: 600,
                fontSize: "14px",
                '&:hover': {
                  backgroundColor: "rgba(211, 47, 47, 0.04) !important",
                },
                '&:disabled': {
                  border: "1.5px solid #ccc !important",
                  color: "#999 !important",
                }
              }}
            >
              {loading && actionType === 'reject' ? (
                <CircularProgress size={20} sx={{ color: '#d32f2f' }} />
              ) : (
                'Reject'
              )}
            </Button>
          )}

          {/* Approve Button */}
          {canApprove && (
            <Button
              variant="contained"
              onClick={handleApprove}
              disabled={loading}
              sx={{
                width: "125px !important",
                height: "36px !important",
                backgroundColor: "#2e7d32 !important",
                border: "1.5px solid #2e7d32 !important",
                color: "#fff !important",
                boxShadow: "none !important",
                textTransform: "none",
                borderRadius: "5px !important",
                fontWeight: 600,
                fontSize: "14px",
                '&:hover': {
                  backgroundColor: "#1e5a22 !important",
                },
                '&:disabled': {
                  backgroundColor: "#ccc !important",
                  border: "1.5px solid #ccc !important",
                  color: "#666 !important",
                }
              }}
            >
              {loading && actionType === 'approve' ? (
                <CircularProgress size={20} sx={{ color: '#fff' }} />
              ) : (
                'Approve'
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChangeRequestModal;